import { useRef, useState } from "react";
import { post_escribir_instruccion, post_reformular_proyecto } from "../../services/api";
import { AI_CONFIG } from "../../components/MarkDownText/config/editorConstants";
import { iUser } from "../../interfaces/Auth/iUser";
import { useChatSession } from "./useChatSession";
import { AIRequestBody } from "../../interfaces/Editor/AIRequestBody";

export type ChatInstructionResult = {
  responseText: string; 
  changeDoc: string; 
};

export const useEditorAI = (user: iUser, setLoadingResponse: (val: boolean) => void, proyectoId:string, resultadoId:string) => {
  const [isLoading, setIsLoading] = useState(false);
  const { session, startChatSession, addChatMessage, updateSession } = useChatSession(user);
  const sessionCreatedRef = useRef(false);

  const buildBody = (prompt: string, contexto: string, instruccion = "Editar", chatSessionId?: string): AIRequestBody => ({
    usuario: user,
    contenidos: {
      temperatura: AI_CONFIG.TEMPERATURE,
      modelID: AI_CONFIG.MODEL_ID,
      prompt,
      contexto,
      instruccion,
      chatSessionId,
    },
  });

  /**
   * Ejecuta una instrucción genérica (compartido entre sendInstruction y reformulate)
   */
  const executeAIInstruction = async (prompt: string, contexto: string, instruccion: string, endpoint: "escribir" | "reformular") => {
    setIsLoading(true);
    setLoadingResponse(true);

    try {
      console.log({ proyectoId, resultadoId });
      console.log({ prompt, contexto, instruccion });
      let sessionId: string | undefined = session?.id;
      // Crear sesión solo la primera vez
      if (!sessionCreatedRef.current) {
        const newSession = await startChatSession("pending", proyectoId, resultadoId);
        sessionCreatedRef.current = true;
        sessionId = newSession.id;
        console.log("Sesión creada:", newSession.id);
      }

      await addChatMessage("user", contexto, prompt);

      const body = buildBody(prompt, contexto, instruccion, sessionId);
      //Función de posteo según el endpoint para llamar al backend
      const postFn = endpoint === "escribir" ? post_escribir_instruccion : post_reformular_proyecto;

      const { data, status } = await postFn(body);
      if (status !== 200) throw new Error("Error en respuesta del modelo");

      const nuevoTexto = data?.respuesta?.cambio ?? ""; 
      const textoInstruccion = data?.respuesta?.response ?? "";     
      const modelo = data?.modelo ?? "desconocido";
      const { input_tokens, output_tokens, total_usd } = data?.costos ?? {
        input_tokens: 0,
        output_tokens: 0,
        total_usd: 0,
      };

      // Actualizar la sesión con los datos del modelo y costos
      await updateSession({
        modelName: modelo,
        totalTokens: input_tokens + output_tokens,
        creditosDescontados: Math.round(total_usd * 1_000_000) //microdólares, para obtener luego el valor se debe dividir entre 1_000_000
      });

      if (nuevoTexto) {
        await addChatMessage("assistant", nuevoTexto, textoInstruccion, output_tokens);
      }

      return {
        texto: nuevoTexto,        
        responseText: textoInstruccion,
        modelo,
        tokensIn: input_tokens,
        tokensOut: output_tokens,
        costoUSD: total_usd,
      };
    } catch (error) {
      console.error("Error en executeAIInstruction:", error);
      return { texto: "", modelo: "", tokensIn: 0, tokensOut: 0, costoUSD: 0 };
    } finally {
      setIsLoading(false);
      setLoadingResponse(false);
    }
  };

  /**
   * Enviar una instrucción normal (edición completa)
   */
  const sendInstruction = async (prompt: string, contexto: string, instruccion = "Editar") => {
    return await executeAIInstruction(prompt, contexto, instruccion, "escribir");
  };

  /**
   * Reformular texto (a partir de selección o instrucción específica)
   */
  const reformulate = async (prompt: string, contexto: string, instruccion: string) => {
    const result = await executeAIInstruction(prompt, contexto, instruccion, "reformular");
    return result.texto;
  };

  const chatInstruction = async (prompt: string, contexto: string, instruccion: string): Promise<ChatInstructionResult> => {
      const { responseText, texto:changeDoc } = await executeAIInstruction(prompt, contexto, instruccion, "reformular");        
      return { responseText: responseText ?? "", changeDoc: changeDoc ?? "" };    
  };

  return { isLoading, sendInstruction, reformulate, chatInstruction };
};
